import { motion } from "framer-motion";
import useLabels from "hooks/useLabels";
import React from "react";
import { getCDLColors } from "../../../design_system/utils/colors";

/**
 * Props for the EvidenceStrengthIndicator component
 */
type EvidenceStrengthIndicatorProps = {
  /** Numerical score representing the strength of evidence (0-10 scale) */
  number: number;
  animated?: boolean;
  /** The variant of the evidence strength indicator */
  variant?: VerticalBarsProps["variant"];
};

/**
 * Enum representing the different evidence strength classes
 */
enum EvidenceStrengthClass {
  Weak = "weak",
  Moderate = "moderate",
  Strong = "strong",
}

/**
 * Metadata for the evidence strength classes
 */
const EvidenceStrengthMetadata = {
  [EvidenceStrengthClass.Weak]: {
    label: "Weak",
    cutoff: 3,
    color: getCDLColors().chartRedBase,
  },
  [EvidenceStrengthClass.Moderate]: {
    label: "Moderate",
    cutoff: 7,
    color: getCDLColors().chartYellowBase,
  },
  [EvidenceStrengthClass.Strong]: {
    label: "Strong",
    cutoff: 10,
    color: getCDLColors().chartGreenBase,
  },
} as const;

/**
 * Props for the VerticalBars sub-component
 */
type VerticalBarsProps = {
  /** The evidence strength score to visualize */
  score?: number;
  /** Metadata object containing color and label information */
  metadata?: (typeof EvidenceStrengthMetadata)[EvidenceStrengthClass];
  /** Whether to animate the bars on mount */
  animated?: boolean;
  /** The variant of the evidence strength indicator */
  variant?: "flat" | "gradient";
};

/**
 * Internal component that renders the vertical bar visualization
 * for evidence strength scores
 */
const VerticalBars: React.FC<VerticalBarsProps> = ({
  score = 0,
  metadata,
  animated = false,
  variant = "gradient",
}) => {
  const totalBars =
    EvidenceStrengthMetadata[EvidenceStrengthClass.Strong].cutoff;

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: totalBars }).map((_, index) => {
        const isActive = index < score;
        const backgroundColor =
          isActive && metadata ? metadata.color : "var(--color-bg-subtle)";

        const barStyle =
          variant === "gradient" && isActive
            ? ({
                "--base-background": backgroundColor,
              } as React.CSSProperties & { [key: string]: string })
            : {
                backgroundColor,
              };

        const barClassName = "flex w-2 h-6 rounded-full vertical-dark-gradient";

        if (animated && metadata) {
          return (
            <motion.div
              key={index}
              initial={{ backgroundColor: "var(--color-bg-subtle)" }}
              whileInView={{ backgroundColor }}
              viewport={{ once: true }}
              transition={{
                duration: 0.25,
                delay: 0.5 + index * 0.2,
                type: "easeOut",
              }}
              style={barStyle}
              className={barClassName}
            />
          );
        }

        return <div key={index} style={barStyle} className={barClassName} />;
      })}
    </div>
  );
};

/**
 * Determines the evidence strength classification based on the numerical score
 *
 * @param number - The evidence strength score
 * @returns The appropriate strength classification
 */
const getStrengthClass = (number: number) => {
  return number <= EvidenceStrengthMetadata[EvidenceStrengthClass.Weak].cutoff
    ? EvidenceStrengthClass.Weak
    : number <= EvidenceStrengthMetadata[EvidenceStrengthClass.Moderate].cutoff
      ? EvidenceStrengthClass.Moderate
      : EvidenceStrengthClass.Strong;
};

/**
 * A visual component that displays evidence strength using animated vertical bars
 * and a text label. Used in research analysis to show the quality or reliability
 * of evidence supporting particular claims or findings.
 *
 * The component uses a 0-10 scale where:
 * - 0-3: Weak evidence (red bars)
 * - 4-7: Moderate evidence (yellow bars)
 * - 8-10: Strong evidence (green bars)
 *
 */
function EvidenceStrengthIndicator({
  number,
  animated = true,
  variant = "gradient",
}: EvidenceStrengthIndicatorProps) {
  const strengthClass = getStrengthClass(number);

  const metadata = EvidenceStrengthMetadata[strengthClass];

  return (
    <span className="flex flex-col gap-2 mt-1">
      <VerticalBars
        score={number}
        metadata={metadata}
        animated={animated}
        variant={variant}
      />
      <span className="text-fg-muted tiny-normal">{metadata.label}</span>
    </span>
  );
}

export const EvidenceStrengthIndicatorExport = ({
  number,
}: EvidenceStrengthIndicatorProps) => {
  return (
    <EvidenceStrengthIndicator
      number={number}
      animated={false}
      variant="flat"
    />
  );
};

/**
 * Placeholder component shown while evidence strength data is loading
 */
export const EvidenceStrengthIndicatorPlaceholder = () => {
  return (
    <span className="flex flex-col gap-2 mt-1">
      <VerticalBars />
    </span>
  );
};

/**
 * Converts evidence strength data to markdown format for plain text export
 *
 * @param props - Evidence strength props containing the numerical score
 * @returns Formatted markdown string with strength label and score
 */
export const EvidenceStrengthIndicatorMarkdown = ({
  number,
}: EvidenceStrengthIndicatorProps) => {
  const [proAnalysisLabels] = useLabels("pro-analysis");

  const strengthClass = getStrengthClass(number);
  const metadata = EvidenceStrengthMetadata[strengthClass];

  return proAnalysisLabels["evidence-strength-plaintext"]
    .replace("{LABEL}", metadata.label)
    .replace("{SCORE}", number.toString());
};

export default EvidenceStrengthIndicator;
